/**
 * Admin Payments panel (Bug 10 followup).
 *
 * Lists all payment methods (Zelle, Venmo, PayPal, Free, Pay-at-venue) with:
 *   - enable toggle
 *   - display name
 *   - handle (email/phone/URL depending on method)
 *   - optional QR image upload (GridFS)
 *   - instructions, fee note, refund note
 *
 * Stripe is intentionally NOT rendered when it's not enabled globally on the
 * event (parked per Bug 10 kill-switch). If admin flips `stripe_enabled=true`
 * on the event AND `PAYMENTS_STRIPE_GLOBAL_ENABLED=true`, a Stripe row is
 * shown here too.
 *
 * Save uses the existing endpoint:
 *   PUT /api/admin/events/{event_id}/payment-methods/{method}
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Save, Upload, Trash2, CheckCircle2, DollarSign, QrCode, Info,
} from 'lucide-react';
import api from '@/utils/api';

const METHOD_META = {
  zelle:   { label: 'Zelle',        accent: 'purple',   handleLabel: 'Email or phone', handlePlaceholder: 'payments@dostination.com' },
  venmo:   { label: 'Venmo',        accent: 'blue',     handleLabel: '@handle',        handlePlaceholder: '@dostination' },
  paypal:  { label: 'PayPal',       accent: 'sky',      handleLabel: 'PayPal.me link or email', handlePlaceholder: 'https://paypal.me/dostination' },
  free:    { label: 'Free event',   accent: 'green',    handleLabel: null,             handlePlaceholder: null },
  stripe:  { label: 'Stripe (Card)',accent: 'indigo',   handleLabel: null,             handlePlaceholder: null, notes: 'Managed via Stripe dashboard' },
};

const METHOD_ORDER = ['zelle', 'venmo', 'paypal', 'free', 'stripe'];

const emptyMethodDoc = (method) => ({
  method,
  enabled: false,
  display_name: METHOD_META[method]?.label || method,
  handle: '',
  qr_media_id: null,
  qr_url: '',
  instructions: '',
  fee_note: '',
  refund_note: '',
});

const PaymentsPanel = ({ eventId, event }) => {
  const [methods, setMethods] = useState({}); // { zelle: {...}, ... }
  const [saving, setSaving] = useState({});
  const [savedTick, setSavedTick] = useState({});

  const stripeGlobalOff = true; // Bug 10: assume off unless env exposes; we hide unless explicitly enabled per event
  const stripeEnabledForEvent = !!(event && event.stripe_enabled);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${eventId}/payment-methods`);
      const map = {};
      (data.methods || []).forEach((r) => { map[r.method] = { ...emptyMethodDoc(r.method), ...r }; });
      METHOD_ORDER.forEach((m) => { if (!map[m]) map[m] = emptyMethodDoc(m); });
      setMethods(map);
    } catch (e) {
      /* non-fatal */
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const updateField = (method, patch) => {
    setMethods((cur) => ({ ...cur, [method]: { ...cur[method], ...patch } }));
  };

  const save = async (method) => {
    setSaving((s) => ({ ...s, [method]: true }));
    try {
      const doc = methods[method];
      await api.put(`/admin/events/${eventId}/payment-methods/${method}`, {
        enabled: doc.enabled,
        display_name: doc.display_name,
        handle: doc.handle || null,
        qr_media_id: doc.qr_media_id || null,
        instructions: doc.instructions || null,
        fee_note: doc.fee_note || null,
        refund_note: doc.refund_note || null,
      });
      setSavedTick((t) => ({ ...t, [method]: true }));
      setTimeout(() => setSavedTick((t) => ({ ...t, [method]: false })), 2000);
    } finally {
      setSaving((s) => ({ ...s, [method]: false }));
    }
  };

  const visibleMethods = METHOD_ORDER.filter((m) => {
    if (m === 'stripe') return stripeEnabledForEvent && !stripeGlobalOff;
    return true;
  });

  const activeMethods = visibleMethods.filter((m) => methods[m]?.enabled);

  return (
    <div className="space-y-6" data-testid="admin-payments-panel">
      {/* Active methods preview */}
      <Card className="border shadow-soft bg-secondary-50/40">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-secondary-700" />
            <div className="text-[11px] uppercase tracking-widest text-secondary-800 font-semibold">
              Live on RSVP Step 5
            </div>
          </div>
          {activeMethods.length === 0 ? (
            <div className="text-sm text-neutral-700 italic">
              No payment methods enabled yet. Guests will see a &quot;contact organizer&quot; state on Step 5. Enable at least one below.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="active-methods-preview">
              {activeMethods.map((m) => (
                <Badge key={m} className="bg-white border border-secondary-300 text-secondary-900 gap-1.5" variant="outline">
                  <CheckCircle2 className="h-3 w-3 text-success-600" />
                  {methods[m]?.display_name || METHOD_META[m]?.label}
                  {methods[m]?.handle && (
                    <span className="text-neutral-500 font-mono text-[10px]">{methods[m].handle}</span>
                  )}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stripe parked notice */}
      {!stripeEnabledForEvent && (
        <div className="flex items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-700">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-neutral-500" />
          <div>
            <strong>Stripe is currently parked.</strong> Zelle and Venmo are the primary methods.
            To enable Stripe on this event, flip <code>stripe_enabled=true</code> on the event and set the
            <code> PAYMENTS_STRIPE_GLOBAL_ENABLED=true</code> environment variable.
          </div>
        </div>
      )}

      {/* Per-method cards */}
      {visibleMethods.map((m) => (
        <MethodCard
          key={m}
          method={m}
          data={methods[m] || emptyMethodDoc(m)}
          onChange={(patch) => updateField(m, patch)}
          onSave={() => save(m)}
          saving={saving[m]}
          saved={savedTick[m]}
        />
      ))}
    </div>
  );
};

const MethodCard = ({ method, data, onChange, onSave, saving, saved }) => {
  const meta = METHOD_META[method] || { label: method, handleLabel: 'Handle' };
  const qrInputRef = useRef(null);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [qrErr, setQrErr] = useState('');

  const uploadQr = async (file) => {
    if (!file) return;
    setUploadingQr(true); setQrErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('owner_type', 'event_gallery');
      fd.append('is_public', 'true');
      const { data: resp } = await api.post('/media/upload/v2', fd);
      onChange({ qr_media_id: resp.id, qr_url: resp.url || resp.public_url });
    } catch (e) {
      setQrErr(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingQr(false);
    }
  };

  return (
    <Card
      className={`border shadow-soft transition-all ${data.enabled ? 'border-secondary-300' : 'border-neutral-200 opacity-90'}`}
      data-testid={`method-card-${method}`}
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-lg bg-${meta.accent}-100 flex items-center justify-center`}>
              <DollarSign className={`h-4 w-4 text-${meta.accent}-700`} />
            </div>
            <div>
              <div className="font-display text-lg font-semibold text-primary-900">{meta.label}</div>
              {meta.notes && <div className="text-xs text-neutral-500">{meta.notes}</div>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor={`enable-${method}`} className="text-xs text-neutral-600">
              {data.enabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id={`enable-${method}`}
              checked={!!data.enabled}
              onCheckedChange={(v) => onChange({ enabled: v })}
              data-testid={`toggle-${method}`}
            />
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Display name" hint="Shown to guests on RSVP Step 5.">
            <Input
              value={data.display_name || ''}
              onChange={(e) => onChange({ display_name: e.target.value })}
              placeholder={meta.label}
              data-testid={`display-name-${method}`}
            />
          </Field>
          {meta.handleLabel && (
            <Field label={meta.handleLabel}>
              <Input
                value={data.handle || ''}
                onChange={(e) => onChange({ handle: e.target.value })}
                placeholder={meta.handlePlaceholder}
                data-testid={`handle-${method}`}
              />
            </Field>
          )}
        </div>

        <Field label="Instructions" hint="What guests should include in the memo / note when paying.">
          <Textarea
            rows={2}
            value={data.instructions || ''}
            onChange={(e) => onChange({ instructions: e.target.value })}
            placeholder="Include your name and the event name in the memo."
            data-testid={`instructions-${method}`}
          />
        </Field>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Fee note (optional)">
            <Input
              value={data.fee_note || ''}
              onChange={(e) => onChange({ fee_note: e.target.value })}
              placeholder="Zero fees" data-testid={`fee-note-${method}`}
            />
          </Field>
          <Field label="Refund note (optional)">
            <Input
              value={data.refund_note || ''}
              onChange={(e) => onChange({ refund_note: e.target.value })}
              placeholder="Refunds via same channel within 5 days" data-testid={`refund-note-${method}`}
            />
          </Field>
        </div>

        {/* QR image */}
        {method !== 'free' && method !== 'stripe' && (
          <Field label="QR code image (optional)" hint="Displayed on RSVP Step 5 so guests can scan.">
            <div className="flex items-center gap-3">
              {data.qr_url ? (
                <div className="relative w-24 h-24 rounded-md overflow-hidden border border-neutral-200 bg-white">
                  <img src={data.qr_url} alt={`${meta.label} QR`} className="w-full h-full object-contain" />
                  <button
                    type="button"
                    onClick={() => onChange({ qr_media_id: null, qr_url: '' })}
                    className="absolute top-1 right-1 p-1 rounded bg-white/95 text-accent-700 hover:bg-accent-50"
                    title="Remove QR"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => qrInputRef.current?.click()}
                  disabled={uploadingQr}
                  className="w-24 h-24 rounded-md border-2 border-dashed border-neutral-300 hover:border-secondary-400 hover:bg-secondary-50/30 flex flex-col items-center justify-center text-neutral-500"
                  data-testid={`qr-upload-${method}`}
                >
                  <QrCode className="h-5 w-5 mb-1 opacity-70" />
                  <span className="text-[10px]">{uploadingQr ? 'Uploading…' : 'Upload'}</span>
                </button>
              )}
              <input
                ref={qrInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0]; e.target.value = ''; uploadQr(f);
                }}
                className="hidden"
              />
              {qrErr && <span className="text-xs text-accent-700">{qrErr}</span>}
            </div>
          </Field>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-100">
          {saved && <span className="text-xs text-success-700 mr-1">Saved</span>}
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-primary-800 hover:bg-primary-900 text-white"
            data-testid={`save-${method}`}
          >
            <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-secondary-700">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
  </div>
);

export default PaymentsPanel;
